import {
	Activity,
	ChevronDown,
	ChevronUp,
	Clock,
	Filter,
	Search,
	User,
} from "lucide-react";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import apiClient from "../../api/client";

const ACTION_OPTIONS = [
	{ value: "", label: "Todas as acoes" },
	{ value: "create", label: "Criar" },
	{ value: "update", label: "Atualizar" },
	{ value: "delete", label: "Deletar" },
	{ value: "clone", label: "Clonar" },
	{ value: "send_email", label: "Enviar Email" },
	{ value: "login", label: "Login" },
	{ value: "login_failed", label: "Login Falhou" },
	{ value: "accept", label: "Aceitar" },
	{ value: "reject", label: "Rejeitar" },
	{ value: "password_reset", label: "Reset de Senha" },
	{ value: "deactivate", label: "Desativar" },
	{ value: "generate_token", label: "Gerar Token" },
	{ value: "update_modules", label: "Atualizar Modulos" },
];

const ENTITY_OPTIONS = [
	{ value: "", label: "Todas as entidades" },
	{ value: "proposal", label: "Proposta" },
	{ value: "client", label: "Cliente" },
	{ value: "company", label: "Empresa" },
	{ value: "plan", label: "Plano" },
	{ value: "module", label: "Modulo" },
	{ value: "template", label: "Template" },
	{ value: "settings", label: "Configuracoes" },
	{ value: "user", label: "Usuario" },
];

const ACTION_BADGES = {
	create: "bg-green-600/20 text-green-300 border-green-500/30",
	update: "bg-blue-600/20 text-blue-300 border-blue-500/30",
	delete: "bg-red-600/20 text-red-300 border-red-500/30",
	clone: "bg-purple-600/20 text-purple-300 border-purple-500/30",
	send_email: "bg-amber-600/20 text-amber-300 border-amber-500/30",
	login: "bg-cyan-600/20 text-cyan-300 border-cyan-500/30",
	login_failed: "bg-red-600/20 text-red-400 border-red-500/30",
	accept: "bg-emerald-600/20 text-emerald-300 border-emerald-500/30",
	reject: "bg-rose-600/20 text-rose-300 border-rose-500/30",
	password_reset: "bg-amber-600/20 text-amber-300 border-amber-500/30",
	deactivate: "bg-red-600/20 text-red-300 border-red-500/30",
	generate_token: "bg-indigo-600/20 text-indigo-300 border-indigo-500/30",
	update_modules: "bg-teal-600/20 text-teal-300 border-teal-500/30",
};

const ACTION_LABELS = {
	create: "Criar",
	update: "Atualizar",
	delete: "Deletar",
	clone: "Clonar",
	send_email: "Email",
	login: "Login",
	login_failed: "Login Falhou",
	accept: "Aceitar",
	reject: "Rejeitar",
	password_reset: "Reset Senha",
	deactivate: "Desativar",
	generate_token: "Gerar Token",
	update_modules: "Modulos",
};

const ENTITY_LABELS = {
	proposal: "Proposta",
	client: "Cliente",
	company: "Empresa",
	plan: "Plano",
	module: "Modulo",
	template: "Template",
	settings: "Config",
	user: "Usuario",
	rejection_reason: "Motivo Rejeicao",
};

const DETAIL_LABELS = {
	name: "Nome",
	email: "Email",
	role: "Role",
	is_active: "Ativo",
	proposal_number: "N. Proposta",
	total: "Total",
	client_id: "ID Cliente",
	client_name: "Cliente",
	approver_name: "Aprovador",
	approver_email: "Email Aprovador",
	rejection_reason: "Motivo",
	rejection_observations: "Observacoes",
	cloned_from: "Clonado de",
	to: "Para",
	changed_fields: "Campos Alterados",
	target_user_id: "ID Usuario Alvo",
	module_count: "Qtd Modulos",
};

function formatDate(dateStr) {
	if (!dateStr) return "-";
	const date = new Date(dateStr);
	const day = String(date.getDate()).padStart(2, "0");
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const year = date.getFullYear();
	const hours = String(date.getHours()).padStart(2, "0");
	const minutes = String(date.getMinutes()).padStart(2, "0");
	return `${day}/${month}/${year} ${hours}:${minutes}`;
}

function formatDetailValue(key, value) {
	if (value === null || value === undefined) return "-";
	if (key === "is_active") return value ? "Sim" : "Nao";
	if (key === "total")
		return `R$ ${Number(value).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`;
	if (key === "changed_fields" && Array.isArray(value)) return value.join(", ");
	return String(value);
}

function DetailsCell({ details }) {
	const [expanded, setExpanded] = useState(false);

	if (
		!details ||
		(typeof details === "object" && Object.keys(details).length === 0)
	) {
		return <span className="text-slate-500 text-xs">-</span>;
	}

	const obj =
		typeof details === "string"
			? (() => {
					try {
						return JSON.parse(details);
					} catch {
						return null;
					}
				})()
			: details;
	const entries = obj ? Object.entries(obj) : [];

	if (entries.length === 0) {
		return <span className="text-slate-500 text-xs">-</span>;
	}

	// Show first 2 entries inline, expand for more
	const preview = entries.slice(0, 2);
	const hasMore = entries.length > 2;

	return (
		<div className="max-w-xs">
			<div className="space-y-0.5">
				{(expanded ? entries : preview).map(([key, value]) => (
					<div key={key} className="flex items-baseline gap-1.5 text-xs">
						<span className="text-slate-500 whitespace-nowrap">
							{DETAIL_LABELS[key] || key}:
						</span>
						<span className="text-slate-300 break-words">
							{formatDetailValue(key, value)}
						</span>
					</div>
				))}
			</div>
			{hasMore && (
				<button
					type="button"
					onClick={() => setExpanded(!expanded)}
					className="flex items-center gap-1 text-[10px] text-cyan-400 hover:text-cyan-300 transition-colors mt-1"
				>
					{expanded ? (
						<ChevronUp className="w-3 h-3" />
					) : (
						<ChevronDown className="w-3 h-3" />
					)}
					{expanded ? "Menos" : `+${entries.length - 2} campos`}
				</button>
			)}
		</div>
	);
}

DetailsCell.propTypes = {
	details: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

export default function ActivityLogs() {
	const [logs, setLogs] = useState([]);
	const [loading, setLoading] = useState(true);
	const [page, setPage] = useState(1);
	const [total, setTotal] = useState(0);
	const [search, setSearch] = useState("");
	const [actionFilter, setActionFilter] = useState("");
	const [entityFilter, setEntityFilter] = useState("");
	const [searchInput, setSearchInput] = useState("");
	const limit = 30;

	const fetchLogs = useCallback(async () => {
		try {
			setLoading(true);
			const params = new URLSearchParams({
				page: String(page),
				limit: String(limit),
				search,
				action: actionFilter,
				entity_type: entityFilter,
			});
			const response = await apiClient.get(
				`/activity-logs?${params.toString()}`,
			);
			const result = response.data;
			setLogs(result.data || []);
			setTotal(result.total || 0);
		} catch (error) {
			console.error("Error fetching activity logs:", error);
			setLogs([]);
			setTotal(0);
		} finally {
			setLoading(false);
		}
	}, [page, search, actionFilter, entityFilter]);

	useEffect(() => {
		fetchLogs();
	}, [fetchLogs]);

	useEffect(() => {
		setPage(1);
	}, []);

	const handleSearch = (e) => {
		e.preventDefault();
		setSearch(searchInput);
	};

	const handleKeyDown = (e) => {
		if (e.key === "Enter") {
			setSearch(searchInput);
		}
	};

	const totalPages = Math.ceil(total / limit);
	const startItem = total === 0 ? 0 : (page - 1) * limit + 1;
	const endItem = Math.min(page * limit, total);

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-3">
					<div className="p-2 bg-cyan-600/20 rounded-lg border border-cyan-500/30">
						<Activity className="w-6 h-6 text-cyan-400" />
					</div>
					<div>
						<h1 className="text-2xl font-bold text-white">Log de Atividades</h1>
						<p className="text-sm text-slate-400">
							Historico de acoes realizadas no sistema
						</p>
					</div>
				</div>
				<div className="text-sm text-slate-400 flex items-center gap-2">
					<Clock className="w-4 h-4" />
					{total} registro{total !== 1 ? "s" : ""}
				</div>
			</div>

			{/* Filters */}
			<div className="bg-slate-800/50 backdrop-blur border border-white/5 rounded-xl p-4">
				<div className="flex flex-col md:flex-row gap-4">
					{/* Search */}
					<div className="flex-1">
						<div className="relative">
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
							<input
								type="text"
								value={searchInput}
								onChange={(e) => setSearchInput(e.target.value)}
								onKeyDown={handleKeyDown}
								placeholder="Buscar por usuario, acao, entidade..."
								className="w-full pl-10 pr-4 py-2.5 bg-slate-900/50 border border-white/5 rounded-lg text-sm text-slate-300 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/40 focus:border-cyan-500/40 transition-all"
							/>
						</div>
					</div>

					{/* Action Filter */}
					<div className="flex items-center gap-2">
						<Filter className="w-4 h-4 text-slate-400 hidden md:block" />
						<select
							value={actionFilter}
							onChange={(e) => setActionFilter(e.target.value)}
							className="px-3 py-2.5 bg-slate-900/50 border border-white/5 rounded-lg text-sm text-slate-300 focus:outline-none focus:ring-2 focus:ring-cyan-500/40 focus:border-cyan-500/40 transition-all appearance-none cursor-pointer min-w-[160px]"
						>
							{ACTION_OPTIONS.map((opt) => (
								<option key={opt.value} value={opt.value}>
									{opt.label}
								</option>
							))}
						</select>
					</div>

					{/* Entity Filter */}
					<div>
						<select
							value={entityFilter}
							onChange={(e) => setEntityFilter(e.target.value)}
							className="px-3 py-2.5 bg-slate-900/50 border border-white/5 rounded-lg text-sm text-slate-300 focus:outline-none focus:ring-2 focus:ring-cyan-500/40 focus:border-cyan-500/40 transition-all appearance-none cursor-pointer min-w-[170px]"
						>
							{ENTITY_OPTIONS.map((opt) => (
								<option key={opt.value} value={opt.value}>
									{opt.label}
								</option>
							))}
						</select>
					</div>

					{/* Search Button */}
					<button
						type="button"
						onClick={handleSearch}
						className="px-4 py-2.5 bg-cyan-600/20 hover:bg-cyan-600/30 text-cyan-300 border border-cyan-500/30 rounded-lg text-sm font-medium transition-all active:scale-95 flex items-center gap-2"
					>
						<Search className="w-4 h-4" />
						Buscar
					</button>
				</div>
			</div>

			{/* Table */}
			<div className="bg-slate-800/50 backdrop-blur border border-white/5 rounded-xl overflow-hidden">
				<div className="overflow-x-auto">
					<table className="w-full">
						<thead>
							<tr className="border-b border-white/5">
								<th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">
									<div className="flex items-center gap-1.5">
										<Clock className="w-3.5 h-3.5" />
										Data/Hora
									</div>
								</th>
								<th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">
									<div className="flex items-center gap-1.5">
										<User className="w-3.5 h-3.5" />
										Usuario
									</div>
								</th>
								<th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">
									Acao
								</th>
								<th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">
									Entidade
								</th>
								<th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">
									Detalhes
								</th>
								<th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">
									IP
								</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-white/5">
							{loading ? (
								<tr>
									<td colSpan={6} className="px-4 py-12 text-center">
										<div className="flex flex-col items-center gap-3">
											<div className="w-8 h-8 border-2 border-cyan-500/30 border-t-cyan-400 rounded-full animate-spin" />
											<span className="text-sm text-slate-400">
												Carregando logs...
											</span>
										</div>
									</td>
								</tr>
							) : logs.length === 0 ? (
								<tr>
									<td colSpan={6} className="px-4 py-12 text-center">
										<div className="flex flex-col items-center gap-3">
											<Activity className="w-8 h-8 text-slate-500" />
											<span className="text-sm text-slate-400">
												Nenhum log encontrado
											</span>
											<span className="text-xs text-slate-500">
												Tente ajustar os filtros de busca
											</span>
										</div>
									</td>
								</tr>
							) : (
								logs.map((log, idx) => (
									<tr
										key={log.id || idx}
										className="hover:bg-white/[0.02] transition-colors"
									>
										{/* Data/Hora */}
										<td className="px-4 py-3">
											<span className="text-sm text-slate-300 whitespace-nowrap">
												{formatDate(log.created_at)}
											</span>
										</td>

										{/* Usuario */}
										<td className="px-4 py-3">
											<span className="text-sm text-slate-300 font-medium">
												{log.user_name || "-"}
											</span>
										</td>

										{/* Acao */}
										<td className="px-4 py-3">
											<span
												className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${
													ACTION_BADGES[log.action] ||
													"bg-slate-600/20 text-slate-300 border-slate-500/30"
												}`}
											>
												{ACTION_LABELS[log.action] || log.action}
											</span>
										</td>

										{/* Entidade */}
										<td className="px-4 py-3">
											<div className="text-sm">
												<span className="text-cyan-400 font-medium">
													{ENTITY_LABELS[log.entity_type] ||
														log.entity_type ||
														"-"}
												</span>
												{log.entity_id && (
													<span className="text-slate-500 ml-1">
														#{log.entity_id}
													</span>
												)}
											</div>
										</td>

										{/* Detalhes */}
										<td className="px-4 py-3">
											<DetailsCell details={log.details} />
										</td>

										{/* IP */}
										<td className="px-4 py-3">
											<code className="text-xs text-slate-400 font-mono">
												{log.ip_address || "-"}
											</code>
										</td>
									</tr>
								))
							)}
						</tbody>
					</table>
				</div>

				{/* Pagination */}
				{total > 0 && (
					<div className="flex items-center justify-between px-4 py-3 border-t border-white/5">
						<span className="text-sm text-slate-400">
							Mostrando {startItem}-{endItem} de {total}
						</span>
						<div className="flex items-center gap-2">
							<button
								type="button"
								onClick={() => setPage((p) => Math.max(1, p - 1))}
								disabled={page <= 1}
								className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
									page <= 1
										? "bg-slate-700/30 text-slate-500 cursor-not-allowed"
										: "bg-slate-700/50 text-slate-300 hover:bg-slate-700/70 active:scale-95"
								}`}
							>
								Anterior
							</button>
							<span className="text-sm text-slate-400 px-2">
								{page} / {totalPages}
							</span>
							<button
								type="button"
								onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
								disabled={page >= totalPages}
								className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
									page >= totalPages
										? "bg-slate-700/30 text-slate-500 cursor-not-allowed"
										: "bg-slate-700/50 text-slate-300 hover:bg-slate-700/70 active:scale-95"
								}`}
							>
								Proximo
							</button>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
